/**
 * Widget météo (Open-Meteo) : conditions actuelles + indicateurs activables
 * (humidité, vent, rafales, pression, point de rosée, stress thermique THI, UV,
 * pluie, lever/coucher) + prévisions horaires et journalières. Le rendu s'adapte
 * aux indicateurs cochés et à la taille du widget.
 */
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useLayoutEffect, useRef, useState } from "react";
import { WeatherData } from "../../../../api/weather";
import { WidgetPreviewContext, WidgetShell } from "./WidgetShell";
import { useWeather } from "./useWeather";
import {
  computeThi,
  degreesToCardinal,
  getWeatherDisplay,
  thiLevel,
  uvLevel,
} from "./weatherCodes";

export type WeatherWidgetProps = {
  latitude?: number;
  longitude?: number;
  locationName?: string;
  showFeelsLike?: boolean;
  showMinMax?: boolean;
  showHumidity?: boolean;
  showWind?: boolean;
  showWindGusts?: boolean;
  showPressure?: boolean;
  showDewPoint?: boolean;
  showThi?: boolean;
  showUv?: boolean;
  showRainProbability?: boolean;
  showSunTimes?: boolean;
  showHourly?: boolean;
  showDaily?: boolean;
};

/** Données factices pour les aperçus (catalogue / configurateur sans localisation). */
const MOCK_WEATHER: WeatherData = {
  timezone: "Europe/Paris",
  current: {
    temperature: 18,
    apparentTemperature: 16,
    humidity: 64,
    precipitation: 0,
    weatherCode: 1,
    windSpeed: 12,
    windDirection: 315,
    windGusts: 28,
    pressure: 1013,
    dewPoint: 11,
    uvIndex: 4,
    isDay: true,
    time: "2026-06-26T15:00",
  },
  hourly: [
    { time: "2026-06-26T15:00", temperature: 18, weatherCode: 0 },
    { time: "2026-06-26T16:00", temperature: 17, weatherCode: 1 },
    { time: "2026-06-26T17:00", temperature: 17, weatherCode: 2 },
    { time: "2026-06-26T18:00", temperature: 15, weatherCode: 3 },
    { time: "2026-06-26T19:00", temperature: 14, weatherCode: 3 },
    { time: "2026-06-26T20:00", temperature: 13, weatherCode: 61 },
  ],
  daily: [
    { date: "2026-06-26", weatherCode: 1, tempMax: 21, tempMin: 12, precipitationProbability: 10, sunrise: "2026-06-26T06:12", sunset: "2026-06-26T21:45" },
    { date: "2026-06-27", weatherCode: 3, tempMax: 19, tempMin: 11, precipitationProbability: 40, sunrise: "2026-06-27T06:13", sunset: "2026-06-27T21:45" },
    { date: "2026-06-28", weatherCode: 61, tempMax: 17, tempMin: 10, precipitationProbability: 70, sunrise: "2026-06-28T06:13", sunset: "2026-06-28T21:46" },
    { date: "2026-06-29", weatherCode: 2, tempMax: 20, tempMin: 12, precipitationProbability: 20, sunrise: "2026-06-29T06:14", sunset: "2026-06-29T21:46" },
    { date: "2026-06-30", weatherCode: 0, tempMax: 23, tempMin: 13, precipitationProbability: 0, sunrise: "2026-06-30T06:14", sunset: "2026-06-30T21:46" },
  ],
};

/** HH:MM extrait d'un ISO local Open-Meteo (sans conversion de fuseau). */
const formatClock = (iso: string) => iso.slice(11, 16);

const hourLabel = (iso: string) => `${new Date(iso).getHours()}h`;

const dayLabel = (iso: string, index: number) =>
  index === 0
    ? "Auj."
    : new Intl.DateTimeFormat("fr-FR", { weekday: "short" }).format(
        new Date(iso)
      );

export const WeatherWidget = ({
  latitude,
  longitude,
  locationName = "Météo",
  showFeelsLike = true,
  showMinMax = true,
  showHumidity = true,
  showWind = true,
  showWindGusts = true,
  showPressure = true,
  showDewPoint = true,
  showThi = true,
  showUv = true,
  showRainProbability = true,
  showSunTimes = true,
  showHourly = true,
  showDaily = true,
}: WeatherWidgetProps) => {
  const isPreview = useContext(WidgetPreviewContext);
  const hasLocation = latitude !== undefined && longitude !== undefined;

  const live = useWeather(latitude, longitude);
  const data = hasLocation ? live.data : isPreview ? MOCK_WEATHER : null;
  const loading = hasLocation && live.loading;
  const error = hasLocation && live.error;

  // Largeur disponible : nombre d'heures + colonnes de la grille de métriques.
  const contentRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const subtitle = data
    ? `Mis à jour à ${formatClock(data.current.time)}`
    : undefined;

  const toggles = {
    showFeelsLike,
    showMinMax,
    showHumidity,
    showWind,
    showWindGusts,
    showPressure,
    showDewPoint,
    showThi,
    showUv,
    showRainProbability,
    showSunTimes,
    showHourly,
    showDaily,
  };

  return (
    <WidgetShell title={locationName} subtitle={subtitle}>
      <div ref={contentRef} className="h-full">
        {!hasLocation && !isPreview ? (
          <EmptyState />
        ) : loading && !data ? (
          <LoadingState />
        ) : error && !data ? (
          <ErrorState />
        ) : data ? (
          <WeatherContent data={data} width={width} toggles={toggles} />
        ) : null}
      </div>
    </WidgetShell>
  );
};

type Toggles = Required<
  Pick<
    WeatherWidgetProps,
    | "showFeelsLike"
    | "showMinMax"
    | "showHumidity"
    | "showWind"
    | "showWindGusts"
    | "showPressure"
    | "showDewPoint"
    | "showThi"
    | "showUv"
    | "showRainProbability"
    | "showSunTimes"
    | "showHourly"
    | "showDaily"
  >
>;

type MetricCell = {
  key: string;
  icon: IconProp;
  value: string;
  label: string;
  color?: string;
};

function WeatherContent({
  data,
  width,
  toggles,
}: {
  data: WeatherData;
  width: number;
  toggles: Toggles;
}) {
  const { current, hourly, daily } = data;
  const display = getWeatherDisplay(current.weatherCode, current.isDay);
  const today = daily[0];

  const hourCount = Math.max(4, Math.min(hourly.length, Math.floor(width / 44)));
  const metricCols = width >= 300 ? 3 : 2;

  // Construit la grille de métriques selon les indicateurs cochés.
  const cells: MetricCell[] = [];
  if (toggles.showHumidity)
    cells.push({ key: "hum", icon: "droplet", value: `${current.humidity}%`, label: "Humidité" });
  if (toggles.showWind)
    cells.push({
      key: "wind",
      icon: "wind",
      value: `${Math.round(current.windSpeed)} km/h ${degreesToCardinal(current.windDirection)}`,
      label: "Vent",
    });
  if (toggles.showWindGusts)
    cells.push({ key: "gust", icon: "wind", value: `${Math.round(current.windGusts)} km/h`, label: "Rafales" });
  if (toggles.showPressure)
    cells.push({ key: "pres", icon: "gauge", value: `${Math.round(current.pressure)} hPa`, label: "Pression" });
  if (toggles.showDewPoint)
    cells.push({ key: "dew", icon: "droplet", value: `${Math.round(current.dewPoint)}°`, label: "Point de rosée" });
  if (toggles.showThi) {
    const thi = computeThi(current.temperature, current.humidity);
    const level = thiLevel(thi);
    cells.push({ key: "thi", icon: "temperature-half", value: `${Math.round(thi)} · ${level.label}`, label: "Stress thermique", color: level.color });
  }
  if (toggles.showUv)
    cells.push({ key: "uv", icon: "sun", value: `${Math.round(current.uvIndex)} · ${uvLevel(current.uvIndex)}`, label: "Indice UV" });
  if (toggles.showRainProbability && today?.precipitationProbability != null)
    cells.push({ key: "rain", icon: "umbrella", value: `${today.precipitationProbability}%`, label: "Pluie" });
  if (toggles.showSunTimes && today?.sunrise && today?.sunset)
    cells.push({ key: "sun", icon: "sun", value: `${formatClock(today.sunrise)} / ${formatClock(today.sunset)}`, label: "Soleil" });

  return (
    <div className="flex flex-col gap-3">
      {/* Héro : température + condition (toujours), ressenti / min-max (selon toggles) */}
      <div
        className="rounded-lg p-3 flex items-center justify-between"
        style={{ background: display.background }}
      >
        <div className="min-w-0">
          <p className="text-4xl font-oxaniumMedium text-default-text leading-none">
            {Math.round(current.temperature)}°
          </p>
          <p className="text-sm text-default-text mt-1 truncate">
            {display.label}
          </p>
          {(toggles.showFeelsLike || toggles.showMinMax) && (
            <p className="text-xs text-grey-medium mt-0.5">
              {toggles.showFeelsLike && (
                <>Ressenti {Math.round(current.apparentTemperature)}°</>
              )}
              {toggles.showFeelsLike && toggles.showMinMax && today && " · "}
              {toggles.showMinMax && today && (
                <>
                  ↑{Math.round(today.tempMax)}° ↓{Math.round(today.tempMin)}°
                </>
              )}
            </p>
          )}
        </div>
        <FontAwesomeIcon
          icon={display.icon}
          className="text-5xl shrink-0"
          style={{ color: display.color }}
          aria-label={display.label}
        />
      </div>

      {cells.length > 0 && (
        <div
          className="grid gap-x-3 gap-y-2"
          style={{ gridTemplateColumns: `repeat(${metricCols}, minmax(0, 1fr))` }}
        >
          {cells.map((cell) => (
            <div key={cell.key} className="flex items-center gap-2 min-w-0">
              <FontAwesomeIcon
                icon={cell.icon}
                className="text-grey-medium shrink-0"
                style={cell.color ? { color: cell.color } : undefined}
              />
              <div className="min-w-0">
                <p
                  className="text-sm font-oxaniumMedium truncate leading-tight"
                  style={cell.color ? { color: cell.color } : undefined}
                >
                  {cell.value}
                </p>
                <p className="text-[10px] text-grey-medium truncate leading-tight">
                  {cell.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {toggles.showHourly && hourly.length > 0 && (
        <div className="flex gap-1 border-t border-grey-light pt-2">
          {hourly.slice(0, hourCount).map((slot) => {
            const d = getWeatherDisplay(slot.weatherCode, true);
            return (
              <div
                key={slot.time}
                className="flex-1 min-w-0 flex flex-col items-center gap-1"
              >
                <p className="text-xs text-grey-medium">{hourLabel(slot.time)}</p>
                <FontAwesomeIcon
                  icon={d.icon}
                  className="text-base"
                  style={{ color: d.color }}
                />
                <p className="text-sm font-oxaniumMedium">
                  {Math.round(slot.temperature)}°
                </p>
              </div>
            );
          })}
        </div>
      )}

      {toggles.showDaily && daily.length > 1 && (
        <div className="flex flex-col gap-1.5 border-t border-grey-light pt-2">
          {daily.slice(0, 5).map((day, index) => {
            const d = getWeatherDisplay(day.weatherCode, true);
            return (
              <div
                key={day.date}
                className="flex items-center justify-between text-sm"
              >
                <span className="w-10 text-grey-dark capitalize">
                  {dayLabel(day.date, index)}
                </span>
                <FontAwesomeIcon
                  icon={d.icon}
                  className="text-sm"
                  style={{ color: d.color }}
                />
                <span className="text-grey-medium">
                  {Math.round(day.tempMin)}°
                </span>
                <span className="font-oxaniumMedium w-8 text-right">
                  {Math.round(day.tempMax)}°
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center text-grey-medium gap-2">
      <FontAwesomeIcon icon="location-dot" className="text-2xl" />
      <p className="text-sm">Choisissez une ville pour afficher la météo.</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="h-full flex flex-col gap-3 animate-pulse">
      <div className="h-20 rounded-lg bg-grey-light" />
      <div className="h-4 rounded bg-grey-light w-2/3" />
      <div className="h-10 rounded bg-grey-light" />
    </div>
  );
}

function ErrorState() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center text-grey-medium gap-2">
      <FontAwesomeIcon icon="wind" className="text-2xl" />
      <p className="text-sm">Météo indisponible pour le moment.</p>
    </div>
  );
}
